import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2 } from "lucide-react";

interface CannedResponseManagerProps {
  onSelectResponse: (content: string) => void;
}

export function CannedResponseManager({ onSelectResponse }: CannedResponseManagerProps) {
  const { toast } = useToast();
  const [responses, setResponses] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("general");

  useEffect(() => {
    loadResponses();
  }, []);

  async function loadResponses() {
    try {
      const { data, error } = await supabase
        .from('canned_responses')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true });

      if (error) throw error;
      setResponses(data || []);
    } catch (error) {
      console.error('Error loading responses:', error);
      toast({
        title: "Error",
        description: "Failed to load canned responses",
        variant: "destructive",
      });
    }
  }

  async function handleSave() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (editingResponse) {
        // Update existing response
        const { error } = await supabase
          .from('canned_responses')
          .update({ title, content, category, updated_at: new Date().toISOString() })
          .eq('id', editingResponse.id);

        if (error) throw error;

        toast({ title: "Response Updated" });
      } else {
        // Create new response
        const { error } = await supabase
          .from('canned_responses')
          .insert({ title, content, category, created_by: user.id });

        if (error) throw error;

        toast({ title: "Response Created" });
      }

      loadResponses();
      setModalOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this canned response?')) return;

    try {
      const { error } = await supabase
        .from('canned_responses')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      toast({ title: "Response Deleted" });
      loadResponses();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  }

  function handleEdit(response: any) {
    setEditingResponse(response);
    setTitle(response.title);
    setContent(response.content);
    setCategory(response.category);
    setModalOpen(true);
  }

  function resetForm() {
    setEditingResponse(null);
    setTitle("");
    setContent("");
    setCategory("general");
  }

  const categorizedResponses = responses.reduce((acc: Record<string, any[]>, response) => {
    if (!acc[response.category]) acc[response.category] = [];
    acc[response.category].push(response);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Canned Responses</h3>
        <Button
          size="sm"
          onClick={() => {
            resetForm();
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Response
        </Button>
      </div>

      {Object.entries(categorizedResponses).map(([cat, items]: [string, any[]]) => (
        <div key={cat}>
          <h4 className="text-sm font-medium mb-2 capitalize">{cat}</h4>
          <div className="space-y-2">
            {items.map((response) => (
              <Card key={response.id} className="hover:bg-muted/50 cursor-pointer">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1" onClick={() => onSelectResponse(response.content)}>
                      <h5 className="font-medium text-sm">{response.title}</h5>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {response.content}
                      </p>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(response);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(response.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingResponse ? "Edit" : "Create"} Canned Response
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Quick response title..."
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="workflow">Workflow</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Response Content</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Type your response template..."
                rows={6}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!title || !content}>
              {editingResponse ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
